import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  switchMap,
  take,
} from 'rxjs';
import { Router } from '@angular/router';
import {
  CultureFacade,
  ProductFacade,
  SiteContextFacade,
  UserDataFacade,
} from '@boels/shared-state';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService, REPLY, RESPONSE, UrlSafePipe } from '@boels/helpers';
import { ErrorModel, UserEnum } from '@boels/models';
import { FastifyReply } from 'fastify';

@Injectable()
export class BoelsCustomHttpInterceptor implements HttpInterceptor {
  #response = inject<ResponseInit>(RESPONSE, { optional: true });
  #reply = inject<FastifyReply>(REPLY, { optional: true });

  #router = inject(Router);
  #productFacade = inject(ProductFacade);
  #urlSafePipe = inject(UrlSafePipe);
  #authService = inject(AuthService);
  #siteContextFacade = inject(SiteContextFacade);
  #cultureFacade = inject(CultureFacade);
  #userDataFacade = inject(UserDataFacade);

  #baseSite = toSignal(
    this.#siteContextFacade.getActiveBaseSiteEntity().pipe(filter(Boolean)),
  );
  #culture = toSignal(this.#cultureFacade.getActiveCulture(), {
    initialValue: '',
  });
  #activeLang = toSignal(this.#cultureFacade.getActiveLanguage(), {
    initialValue: '',
  });
  #isAnonymousOrGuestUser = toSignal(
    this.#userDataFacade
      .userData()
      .pipe(map((user) => user.type !== UserEnum.B2C_AUTHENTICATED)),
  );
  #retryCount = 0;
  #noCacheHeader = {
    'Cache-Control': `no-store, no-cache, must-revalidate, max-age=0`,
  };

  intercept(
    httpRequest: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      httpRequest.url.includes('anonymous') &&
      this.#isAnonymousOrGuestUser()
    ) {
      return this.#authService.getToken().pipe(
        mergeMap((token) => {
          const request = httpRequest.clone({
            setHeaders: { Authorization: `Bearer ${token.access_token}` },
          });
          return next.handle(request);
        }),
      );
    }
    return next.handle(httpRequest).pipe(
      filter((event) => event instanceof HttpResponse),
      map((event) => this.#handlePossibleRedirect(event as HttpResponse<any>)),
      catchError((err) => {
        if (err instanceof HttpErrorResponse) {
          switch (err.status) {
            case HttpStatusCode.RequestTimeout:
              return this.#retryRequest(next, httpRequest, err);
            case HttpStatusCode.Found:
              return this.#redirectUsingHeader(err);
            case HttpStatusCode.BadRequest:
              switch (this.#getErrorReason(err)) {
                case 'code':
                  return this.#handlePossiblePDPRedirect(err);
                case 'lang':
                  return this.#handlePossibleUnsupportedLanguage(
                    next,
                    httpRequest,
                    err,
                  );
                default:
                  throw err;
              }
            default:
              throw err;
          }
        }
        throw err;
      }),
    );
  }

  #redirectUsingHeader(error: HttpErrorResponse) {
    const redirectUrl = error.headers?.get('redirecturl');
    const redirectPath = redirectUrl || '/';

    if (this.#reply) {
      this.#reply
        .headers(this.#noCacheHeader)
        .redirect(redirectPath, HttpStatusCode.MovedPermanently);
    } else {
      this.#router.navigateByUrl(redirectPath);
    }
    return EMPTY;
  }

  #retryRequest(
    next: HttpHandler,
    request: HttpRequest<any>,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    if (this.#retryCount === 0) {
      this.#retryCount++;
      return next.handle(request);
    }
    this.#retryCount = 0;
    throw error;
  }

  #handlePossibleRedirect(event: HttpResponse<any>): HttpResponse<any> {
    const foundByRedirect = event?.body?.foundByRedirect;
    if (foundByRedirect && event.body.label) {
      return this.#foundByRedirect(event);
    }
    return event;
  }

  #foundByRedirect(event: HttpResponse<any>): HttpResponse<any> {
    const label = event.body.label;
    const sanitizedLabel =
      label.length && label[0] === '/' ? label.slice(1) : label;

    const newLocation = `/${this.#culture()}/${sanitizedLabel}`;
    if (this.#reply) {
      this.#reply
        .headers(this.#noCacheHeader)
        .redirect(newLocation, HttpStatusCode.MovedPermanently);
    }

    return event.clone({
      status: HttpStatusCode.MovedPermanently,
      headers: event.headers
        .set('location', newLocation)
        .set('Cache-Control', 'no-store, no-cache, must-revalidate'),
    });
  }

  #getErrorReason(error: HttpErrorResponse): 'code' | 'lang' | undefined {
    const errors = error.error.errors;
    const reason: ErrorModel =
      errors && errors.length > 0 ? errors[0] : undefined;
    if (!reason) return undefined;

    if (
      (reason.reason === 'invalid' && reason.subject === 'code') ||
      error.url?.includes('pageType=ProductPage')
    )
      return 'code';

    if (reason && reason.type === 'UnsupportedLanguageError') return 'lang';

    return undefined;
  }

  #handlePossiblePDPRedirect(
    error: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    const queryParams = error.url?.split('?')[1].split('&');

    const codeParam = queryParams?.find((param) => param.startsWith('code='));
    const productCode = codeParam?.split('=')[1];
    if (productCode) {
      return this.#productFacade.getRedirectForProduct(productCode).pipe(
        take(1),
        switchMap((redirectModel) => {
          const breadcrumbs = redirectModel.breadcrumbs;
          const last = breadcrumbs[breadcrumbs.length - 1];

          const path = breadcrumbs
            .map((breadcrumb) => breadcrumb.categoryName)
            .filter((categoryName) => !!categoryName)
            .map((categoryName) => {
              return this.#urlSafePipe.transform(categoryName);
            })
            .reduce((name1, name2) => name1?.concat('/').concat(name2 ?? ''));

          const label =
            `${this.#culture()}/` +
            (path?.concat('/c/').concat(last.categoryCode ?? '') ?? '');

          if (this.#reply) {
            this.#reply
              .headers(this.#noCacheHeader)
              .redirect(
                '/' + label + '?status=pdp_redirect',
                HttpStatusCode.MovedPermanently,
              );
          } else {
            this.#router.navigate([label], {
              queryParams: { status: 'pdp_redirect' },
            });
          }
          return EMPTY;
        }),
        catchError(() => {
          this.#router.navigate([`${this.#culture()}/` + 'not-found']);
          if (this.#response) {
            this.#response.status = HttpStatusCode.NotFound;
            this.#response.headers = {
              ...this.#response.headers,
              'Cache-Control': 'no-store, no-cache, must-revalidate',
              'Content-type': 'text/html;charset=UTF-8',
            };
          }
          return EMPTY;
        }),
      );
    }
    throw error;
  }

  #handlePossibleUnsupportedLanguage(
    next: HttpHandler,
    request: HttpRequest<any>,
    error: HttpErrorResponse,
  ): Observable<HttpEvent<any>> {
    const langParam = request.params.get('lang') || '';
    const isValidLang = (language: string) =>
      this.#baseSite()?.stores?.some((store) =>
        store.languages?.some(
          (lang) => lang.isocode?.split('_')[0] === language,
        ),
      ) || false;

    if (this.#activeLang() === langParam && isValidLang(langParam))
      return this.#retryRequest(next, request, error);

    if (!isValidLang(langParam) && isValidLang(this.#activeLang())) {
      const updatedRequest = request.clone({
        params: request.params.set('lang', this.#activeLang()),
      });
      return this.#retryRequest(next, updatedRequest, error);
    }

    const defaultLang =
      this.#baseSite()?.stores?.[0]?.defaultLanguage?.isocode?.split('_')[0] ||
      undefined;
    if (defaultLang) {
      this.#cultureFacade.setActiveLanguage(defaultLang);
      const updatedRequest = request.clone({
        params: request.params.set('lang', defaultLang),
      });
      return this.#retryRequest(next, updatedRequest, error);
    }
    throw error;
  }
}
